import type { EventPropValue, EventsContext } from './hernasEventsClient';

/** `Chrome 129`, `Safari 18`, `Firefox 131`, `Edge 129`, or `other`. */
export function browserFamily(userAgent: string): string {
  const rules: [string, RegExp][] = [
    ['Edge', /Edg\/(\d+)/],
    ['Opera', /OPR\/(\d+)/],
    ['Firefox', /Firefox\/(\d+)/],
    ['Chrome', /Chrome\/(\d+)/],
    ['Safari', /Version\/(\d+)[.\d]* .*Safari\//],
  ];
  for (const [name, pattern] of rules) {
    const match = pattern.exec(userAgent);
    if (match !== null) {
      return `${name} ${match[1]}`;
    }
  }
  return 'other';
}

/** `phone`, `tablet` or `desktop` (contract v1: the web `deviceModel`). */
export function deviceClass(userAgent: string, width: number): 'phone' | 'tablet' | 'desktop' {
  if (/iPad|Tablet/i.test(userAgent) || (/Android/i.test(userAgent) && !/Mobile/i.test(userAgent))) {
    return 'tablet';
  }
  if (/Mobi|iPhone|Android/i.test(userAgent) || width < 600) {
    return 'phone';
  }
  return 'desktop';
}

export function browserContext(): EventsContext {
  const { userAgent } = navigator;
  const release = process.env.GIT_COMMIT;
  return {
    platform: 'web',
    ...(release !== undefined && release !== '' ? { appVersion: release.slice(0, 64) } : {}),
    osVersion: browserFamily(userAgent),
    deviceModel: deviceClass(userAgent, window.innerWidth),
    locale: navigator.language.slice(0, 35),
  };
}

/**
 * The props of `session_started`: the campaign of the URL that opened the tab and the outside
 * referrer host. No click id and no full URL leave the browser here.
 */
export function sessionProps(firstSession: boolean): Record<string, EventPropValue> {
  const props: Record<string, EventPropValue> = { entry: 'launch', firstSession };
  try {
    const params = new URLSearchParams(window.location.search);
    const pairs: [string, string][] = [
      ['utmSource', 'utm_source'],
      ['utmMedium', 'utm_medium'],
      ['utmCampaign', 'utm_campaign'],
      ['utmContent', 'utm_content'],
    ];
    for (const [prop, param] of pairs) {
      const value = params.get(param);
      if (value !== null && value !== '') {
        props[prop] = value.slice(0, 100);
      }
    }
    if (params.get('utm_source') !== null) {
      props.entry = 'link';
    }
    if (document.referrer !== '') {
      const { host } = new URL(document.referrer);
      if (host !== window.location.host) {
        props.referrerHost = host;
        props.entry = 'link';
      }
    }
  } catch {
    // A bad referrer: the props stay as they are.
  }
  return props;
}
